import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Copy, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isOnSale } from "@/lib/pricing";
import { ParticleField } from "@/components/ParticleField";
import { PackageCard } from "@/components/PackageCard";
import { UsernameDialog } from "@/components/UsernameDialog";

const useCountdown = (endsAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(endsAt).getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
    done: diff <= 0,
  };
};

const CountdownBox = ({ value, label, testid }) => (
  <div className="flex flex-col items-center">
    <span className="font-display text-5xl sm:text-7xl text-white tabular-nums" data-testid={testid}>
      {String(value).padStart(2, "0")}
    </span>
    <span className="mt-2 text-[10px] font-bold tracking-[0.3em] text-slate-500">{label}</span>
  </div>
);

export default function FlashSalePage() {
  const [config, setConfig] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pending, setPending] = useState(null);
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(config.coupon_code);
      setCopied(true);
      toast.success("Code copied", { description: `${config.coupon_code} — apply it at checkout` });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed", { description: `The code is ${config.coupon_code}` });
    }
  };

  useEffect(() => {
    api.get("/flash-sale").then((r) => setConfig(r.data)).catch(() => setConfig({ active: false }));
    api.get("/packages").then((r) => setPackages(r.data.filter(isOnSale))).catch(() => {});
  }, []);

  const countdown = useCountdown(config?.ends_at || new Date().toISOString());

  if (!config) {
    return <main className="min-h-screen bg-[#05030A]" data-testid="flash-sale-page" />;
  }

  const live = config.active && !countdown.done;

  return (
    <main data-testid="flash-sale-page" className="relative min-h-screen bg-[#05030A] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 30%, rgba(139,92,246,0.2), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05030A]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
        <Link
          to="/"
          data-testid="flash-sale-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to store
        </Link>

        {!live ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-24 text-center"
            data-testid="flash-sale-inactive"
          >
            <Zap className="w-12 h-12 text-[#8B5CF6] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-6 font-display text-4xl sm:text-6xl tracking-tight text-white">
              No flash sale <span className="italic text-[#A78BFA]">right now</span>
            </h1>
            <p className="mt-4 text-slate-400 max-w-md mx-auto">
              Flash sales appear without warning and vanish fast. Join the Discord so you never miss one.
            </p>
            <Link
              to="/store"
              data-testid="flash-sale-browse-link"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300"
            >
              BROWSE THE STORE
            </Link>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-16 text-center"
            >
              <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.35em] text-amber-300" data-testid="flash-sale-overline">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
                </span>
                LIMITED TIME — EVERYTHING MUST GO
              </p>
              <h1 className="mt-6 font-display text-glow text-6xl sm:text-8xl tracking-tight bg-gradient-to-br from-[#EDE9FE] via-[#A78BFA] to-[#7C3AED] bg-clip-text text-transparent" data-testid="flash-sale-title">
                {config.title}
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-14 flex items-start justify-center gap-3 sm:gap-5"
              data-testid="flash-countdown"
            >
              <CountdownBox value={countdown.days} label="DAYS" testid="flash-countdown-days" />
              <span className="font-display text-4xl sm:text-6xl text-[#8B5CF6]/60">:</span>
              <CountdownBox value={countdown.hours} label="HOURS" testid="flash-countdown-hours" />
              <span className="font-display text-4xl sm:text-6xl text-[#8B5CF6]/60">:</span>
              <CountdownBox value={countdown.mins} label="MINUTES" testid="flash-countdown-mins" />
              <span className="font-display text-4xl sm:text-6xl text-[#8B5CF6]/60">:</span>
              <CountdownBox value={countdown.secs} label="SECONDS" testid="flash-countdown-secs" />
            </motion.div>

            {config.coupon_code && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="mt-12 flex flex-col items-center gap-3"
                data-testid="flash-coupon-section"
              >
                <button
                  onClick={copyCode}
                  data-testid="flash-coupon-code"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-2xl border-2 border-dashed border-amber-400/60 bg-amber-400/10 hover:bg-amber-400/20 transition-[background-color,border-color] duration-300"
                >
                  <span className="text-[11px] font-bold tracking-[0.3em] text-amber-300">USE CODE</span>
                  <span className="font-display text-2xl sm:text-3xl tracking-[0.1em] text-white">{config.coupon_code}</span>
                  {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5 text-amber-300 group-hover:text-white transition-colors duration-300" />}
                </button>
                <p className="text-xs text-slate-500">Tap to copy — apply it at checkout before the timer hits zero.</p>
              </motion.div>
            )}

            {packages.length > 0 && (
              <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="flash-sale-packages">
                {packages.map((pkg, i) => (
                  <PackageCard key={pkg.id} pkg={pkg} index={i} onPurchase={setPending} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <UsernameDialog pkg={pending} onClose={() => setPending(null)} />
    </main>
  );
}
